import Editor from '@monaco-editor/react';
import {
	AlertCircle,
	CheckCircle,
	ChevronDown,
	ChevronRight,
	Copy,
	Eye,
	EyeOff,
	HelpCircle,
	Loader2,
	Play,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { useIsDark } from '../../contexts/ThemeContext';
import { useNodeValidation } from '../../hooks/useNodeValidation';
import { describeJsonPath, getValueByPath, isValidJsonPath } from '../../lib/json-path-parser';
import {
	getGroupedVariables,
	getStandardizedVariables,
	getVariableDisplayText,
	getVariableReference,
} from '../../lib/variable-display-utils';
import { getAvailableFields } from '../../lib/workflow-utils';
import { ProcessingNodeData } from '../../types/nodes';
import type { ContextField } from '../../types/workflow-context';
import { JsonFormatButton } from '../shared/JsonFormatButton';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { VariablePicker } from '../VariablePicker';

interface ApiCallConfigProps {
	nodeId: string;
	data: ProcessingNodeData;
	onChange: (data: ProcessingNodeData) => void;
	// Wizard state callbacks for parent component
	onWizardStateChange?: (state: {
		wizardMode: boolean;
		currentStep: number;
		totalSteps: number;
		canProceedToNext: boolean;
		onPreviousStep: () => void;
		onNextStep: () => void;
		onCompleteWizard: () => Promise<void>;
		validationAttempted: boolean;
		validationFailed: boolean;
	}) => void;
	onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

interface ApiCallConfig {
	url: string;
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
	headers: { key: string; value: string }[];
	body?: string;
	queryParams: { key: string; value: string }[];
	authentication: {
		type: 'none' | 'basic' | 'bearer';
		username?: string;
		password?: string;
		token?: string;
	};
	requestSchema: Record<string, string>; // fieldName -> type
	responseSchema: Record<string, string>; // fieldName -> type
	responseFields: { key: string; path: string; type: string }[]; // Fields to extract from response
	storeAs?: string; // Custom storage key for response data
	extractPath?: string; // Path to extract from response (e.g., 'data.result')
	timeout?: number; // Request timeout in milliseconds
	retryCount?: number; // Number of retry attempts
}

interface TestResult {
	success: boolean;
	status?: number;
	statusText?: string;
	data?: any;
	error?: string;
	duration: number;
	timestamp: string;
}

const defaultConfig: ApiCallConfig = {
	url: '',
	method: 'POST',
	headers: [],
	queryParams: [],
	authentication: {
		type: 'none',
	},
	requestSchema: {},
	responseSchema: {},
	responseFields: [],
	timeout: 30000,
	retryCount: 0,
};

// Wizard Step Components
interface WizardStepProps {
	config: ApiCallConfig;
	nodeId: string;
	handleChange: (newConfig: Partial<ApiCallConfig>) => void;
	getAvailableTokens?: () => Record<string, ContextField[]>;
	copyToClipboard?: (text: string) => void;
}

// Step 1: Configure Endpoint
function WizardStep1({
	config,
	nodeId,
	handleChange,
	handleAuthChange,
	addHeader,
	removeHeader,
	addQueryParam,
	removeQueryParam,
	getAvailableTokens,
	copyToClipboard,
}: WizardStepProps & {
	handleAuthChange: (authChanges: Partial<ApiCallConfig['authentication']>) => void;
	addHeader: () => void;
	removeHeader: (index: number) => void;
	addQueryParam: () => void;
	removeQueryParam: (index: number) => void;
}) {
	// URL validation state
	const [urlValidation, setUrlValidation] = useState<{
		isValid: boolean;
		error?: string;
		warning?: string;
	}>({ isValid: false });

	// Validate URL when it changes
	useEffect(() => {
		if (config.url) {
			const validation = validateUrlFormat(config.url);
			setUrlValidation(validation);
		} else {
			setUrlValidation({ isValid: false });
		}
	}, [config.url]);

	// URL validation function (same as in parent component)
	const validateUrlFormat = (
		url: string
	): { isValid: boolean; error?: string; warning?: string } => {
		if (!url || url.trim() === '') {
			return { isValid: false, error: 'URL is required' };
		}

		const trimmedUrl = url.trim();

		// Check for whitespace issues
		if (trimmedUrl !== url) {
			return {
				isValid: false,
				warning: 'URL should not have leading or trailing whitespace',
			};
		}

		try {
			const urlObj = new URL(trimmedUrl);

			// Check protocol
			if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
				return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' };
			}

			// Check hostname
			if (!urlObj.hostname || urlObj.hostname.length === 0) {
				return { isValid: false, error: 'URL must have a valid hostname' };
			}

			// Check for valid hostname format
			const hostnameRegex =
				/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
			if (!hostnameRegex.test(urlObj.hostname)) {
				return { isValid: false, error: 'URL has an invalid hostname format' };
			}

			// Check for localhost or private IP addresses (security warning)
			const isLocalhost =
				urlObj.hostname === 'localhost' ||
				urlObj.hostname === '127.0.0.1' ||
				urlObj.hostname === '::1' ||
				urlObj.hostname.startsWith('192.168.') ||
				urlObj.hostname.startsWith('10.') ||
				urlObj.hostname.startsWith('172.') ||
				urlObj.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);

			if (isLocalhost) {
				return {
					isValid: true,
					warning:
						'URL points to localhost/private IP - may not be accessible in production',
				};
			}

			// Check for common security issues
			if (urlObj.hostname.includes('..') || urlObj.hostname.includes('--')) {
				return {
					isValid: false,
					error: 'URL contains potentially malicious hostname patterns',
				};
			}

			// Check port if specified
			if (urlObj.port) {
				const port = parseInt(urlObj.port, 10);
				if (isNaN(port) || port < 1 || port > 65535) {
					return { isValid: false, error: 'URL has an invalid port number' };
				}
			}

			// Check for extremely long URLs
			if (trimmedUrl.length > 2048) {
				return { isValid: false, error: 'URL is too long (maximum 2048 characters)' };
			}

			return { isValid: true };
		} catch (error) {
			return { isValid: false, error: 'URL must be a valid format' };
		}
	};

	return (
		<div className="space-y-6 max-w-4xl">
			{/* Welcome Header */}
			<div className="p-6 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
				<div className="flex gap-3 items-start">
					<div className="text-2xl text-blue-600 dark:text-blue-400">🚀</div>
					<div>
						<h3 className="mb-2 text-lg font-semibold text-blue-900 dark:text-blue-100">
							Configure Your API Endpoint
						</h3>
						<p className="text-sm text-blue-800 dark:text-blue-200">
							Let's start by setting up the basic API connection details. Don't worry
							- we'll guide you through each field!
						</p>
					</div>
				</div>
			</div>

			{/* Storage Key Configuration */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-3 font-medium text-gray-900 dark:text-white">📦 Data Storage</h4>
				<div className="space-y-3">
					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							Storage Key (Optional)
						</label>
						<input
							type="text"
							value={config.storeAs || ''}
							onChange={e => handleChange({ storeAs: e.target.value })}
							placeholder="api_data"
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
						/>
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							Choose a name for how downstream nodes will access this API's data.
							Leave blank to use the default.
						</p>
					</div>
				</div>
			</div>

			{/* URL Configuration */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-3 font-medium text-gray-900 dark:text-white">🌐 API Endpoint</h4>
				<div className="space-y-4">
					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							API URL <span className="text-red-500">*</span>
						</label>
						<VariablePicker
							nodeId={nodeId}
							value={config.url}
							onChange={(value: string) => handleChange({ url: value })}
							placeholder="https://api.example.com/users"
							className={`px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border shadow-sm transition-colors dark:bg-gray-800 dark:text-white ${
								urlValidation.error
									? 'border-red-300 dark:border-red-600 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-400'
									: urlValidation.warning
									? 'border-yellow-300 dark:border-yellow-600 focus:ring-2 focus:ring-yellow-500 dark:focus:ring-yellow-400 focus:border-yellow-500 dark:focus:border-yellow-400'
									: 'border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400'
							}`}
						/>

						{/* URL Validation Feedback */}
						{urlValidation.error && (
							<div className="flex gap-2 items-center mt-2 text-sm text-red-600 dark:text-red-400">
								<span className="text-red-500">❌</span>
								{urlValidation.error}
							</div>
						)}

						{urlValidation.warning && !urlValidation.error && (
							<div className="flex gap-2 items-center mt-2 text-sm text-yellow-600 dark:text-yellow-400">
								<span className="text-yellow-500">⚠️</span>
								{urlValidation.warning}
							</div>
						)}

						{urlValidation.isValid && !urlValidation.warning && (
							<div className="flex gap-2 items-center mt-2 text-sm text-green-600 dark:text-green-400">
								<span className="text-green-500">✅</span>
								URL format is valid
							</div>
						)}

						{!config.url.trim() && (
							<p className="mt-1 text-sm text-red-600 dark:text-red-400">
								⚠️ API URL is required to proceed
							</p>
						)}
					</div>

					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							HTTP Method
						</label>
						<select
							value={config.method}
							onChange={e =>
								handleChange({ method: e.target.value as ApiCallConfig['method'] })
							}
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
						>
							<option value="GET">GET - Retrieve data</option>
							<option value="POST">POST - Create new data</option>
							<option value="PUT">PUT - Update existing data</option>
							<option value="DELETE">DELETE - Remove data</option>
							<option value="PATCH">PATCH - Partial update</option>
						</select>
					</div>
				</div>
			</div>

			{/* Authentication */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-3 font-medium text-gray-900 dark:text-white">
					🔐 Authentication
				</h4>
				<div className="space-y-4">
					<div>
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							Authentication Type
						</label>
						<select
							value={config.authentication.type}
							onChange={e => {
								const type = e.target
									.value as ApiCallConfig['authentication']['type'];
								handleAuthChange({
									type,
									...(type === 'basic' && { username: '', password: '' }),
									...(type === 'bearer' && { token: '' }),
								});
							}}
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
						>
							<option value="none">No Authentication</option>
							<option value="basic">Basic Auth (Username/Password)</option>
							<option value="bearer">Bearer Token (API Key)</option>
						</select>
					</div>

					{config.authentication.type === 'basic' && (
						<div className="grid grid-cols-2 gap-4">
							<VariablePicker
								nodeId={nodeId}
								value={config.authentication.username || ''}
								onChange={(value: string) => handleAuthChange({ username: value })}
								placeholder="Username"
								className="px-4 py-3 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
							/>
							<VariablePicker
								nodeId={nodeId}
								value={config.authentication.password || ''}
								onChange={(value: string) => handleAuthChange({ password: value })}
								placeholder="Password"
								className="px-4 py-3 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
							/>
						</div>
					)}

					{config.authentication.type === 'bearer' && (
						<VariablePicker
							nodeId={nodeId}
							value={config.authentication.token || ''}
							onChange={(value: string) => handleAuthChange({ token: value })}
							placeholder="Bearer token or API key"
							className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
						/>
					)}
				</div>
			</div>

			{/* Headers (Optional) */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-3 font-medium text-gray-900 dark:text-white">
					📋 Headers (Optional)
				</h4>
				<div className="space-y-3">
					{config.headers.length === 0 ? (
						<p className="text-sm italic text-gray-500 dark:text-gray-400">
							No headers configured. Click "Add Header" below to add custom headers.
						</p>
					) : (
						config.headers.map((header, index) => (
							<div key={index} className="flex gap-3">
								<input
									type="text"
									value={header.key}
									onChange={e => {
										const newHeaders = [...config.headers];
										newHeaders[index].key = e.target.value;
										handleChange({ headers: newHeaders });
									}}
									placeholder="Header name"
									className="flex-1 px-4 py-3 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
								/>
								<VariablePicker
									nodeId={nodeId}
									value={header.value}
									onChange={(value: string) => {
										const newHeaders = [...config.headers];
										newHeaders[index].value = value;
										handleChange({ headers: newHeaders });
									}}
									placeholder="Header value"
									className="flex-1 px-4 py-3 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
								/>
								<button
									onClick={() => removeHeader(index)}
									className="px-3 py-2 text-red-600 rounded-lg transition-colors hover:text-red-800 hover:bg-red-50"
								>
									Remove
								</button>
							</div>
						))
					)}
				</div>
			</div>

			{/* Action Buttons */}
			<div className="flex gap-3">
				<button
					onClick={addHeader}
					className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
				>
					+ Add Header
				</button>
				<button
					onClick={addQueryParam}
					className="px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100"
				>
					+ Add Query Parameter
				</button>
			</div>
		</div>
	);
}

// Step 2: Define Request Payload
function WizardStep2({
	config,
	nodeId,
	handleChange,
	generateRequestSchema,
	getAvailableTokens,
	copyToClipboard,
	getNodes,
	getEdges,
}: WizardStepProps & {
	generateRequestSchema: () => void;
	getNodes: () => any[];
	getEdges: () => any[];
}) {
	// Get validation status from parent component
	const isStep2Valid = () => {
		// Step 2 is optional for GET requests, required for others
		if (config.method === 'GET') return true;

		// For non-GET methods, validate that the request body contains valid JSON
		if (!config.body || !config.body.trim()) {
			return false; // Body is required for non-GET methods
		}

		try {
			// Try to parse as JSON first
			JSON.parse(config.body.trim());
			return true; // Valid JSON
		} catch (parseError) {
			// If it's not valid JSON, try to convert from object-like syntax
			try {
				// Replace unquoted property names with quoted ones
				const withQuotes = config.body
					.trim()
					.replace(/(\s*)(\w+)(\s*):(\s*)/g, '$1"$2"$3:$4');

				// Try to parse the modified content
				JSON.parse(withQuotes);
				return true; // Object-like syntax that can be converted
			} catch (secondError) {
				// If still can't parse, try to evaluate as a JavaScript object
				try {
					// Create a function that returns the object
					const func = new Function(`return ${config.body.trim()}`);
					const parsed = func();

					// Validate that it's actually an object
					if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
						return true; // Valid object-like syntax
					}

					return false; // Not a valid object
				} catch (thirdError) {
					return false; // Invalid syntax
				}
			}
		}
	};

	// Get validation status
	const validation = {
		isValid: isStep2Valid(),
		error:
			!isStep2Valid() && config.method !== 'GET'
				? 'Request body must contain valid JSON for non-GET methods'
				: undefined,
		warning:
			!isStep2Valid() && config.method === 'GET'
				? 'Request body is optional for GET methods'
				: undefined,
	};

	return (
		<div className="space-y-6 max-w-4xl">
			{/* Header */}
			<div className="p-6 bg-purple-50 rounded-lg border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800">
				<div className="flex gap-3 items-start">
					<div className="text-2xl text-purple-600 dark:text-purple-400">📤</div>
					<div>
						<h3 className="mb-2 text-lg font-semibold text-purple-900 dark:text-purple-100">
							Define Request Payload
						</h3>
						<p className="text-sm text-purple-800 dark:text-purple-200">
							{config.method === 'GET'
								? "GET requests typically don't need a body. You can skip this step or add query parameters in the previous step."
								: 'Configure what data to send to the API. This is usually JSON format with your form data and variables.'}
						</p>
					</div>
				</div>
			</div>

			{config.method !== 'GET' && (
				<>
					{/* Quick Start */}
					<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
						<h4 className="mb-3 font-medium text-gray-900 dark:text-white">
							🚀 Quick Start
						</h4>
						<button
							onClick={() => {
								generateRequestSchema();
							}}
							className="px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
						>
							Auto-Generate from Available Data
						</button>
						<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
							This will create a request body using all available form data and
							variables.
						</p>
					</div>

					{/* Request Body */}
					<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
						<div className="flex justify-between items-center mb-3">
							<h4 className="font-medium text-gray-900 dark:text-white">
								📝 Request Body
							</h4>
							<JsonFormatButton
								content={config.body || ''}
								onFormatSuccess={formattedContent => {
									handleChange({ body: formattedContent });
								}}
								onFormatError={error => {
									alert(`Error formatting JSON: ${error}`);
								}}
								buttonText="🔧 Format JSON"
								size="md"
								variant="primary"
								title="Format and validate JSON content"
							/>
						</div>
						<VariablePicker
							nodeId={nodeId}
							value={config.body || ''}
							onChange={(value: string) => handleChange({ body: value })}
							placeholder={JSON.stringify(
								{
									name: '{{form.name}}',
									email: '{{form.email}}',
									amount: '{{form.amount}}',
								},
								null,
								2
							)}
							className={`px-4 py-3 w-full font-mono text-sm text-gray-900 bg-white rounded-lg border shadow-sm dark:bg-gray-800 dark:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 ${
								validation.error
									? 'border-red-300 dark:border-red-600'
									: validation.warning
									? 'border-yellow-300 dark:border-yellow-600'
									: validation.isValid
									? 'border-green-300 dark:border-green-600'
									: 'border-gray-300 dark:border-gray-600'
							}`}
							multiline={true}
						/>

						{/* JSON Validation Feedback */}
						{validation.error && (
							<div className="flex gap-2 items-center mt-2 text-sm text-red-600 dark:text-red-400">
								<span className="text-red-500">❌</span>
								{validation.error}
							</div>
						)}

						{validation.warning && !validation.error && (
							<div className="flex gap-2 items-center mt-2 text-sm text-yellow-600 dark:text-yellow-400">
								<span className="text-yellow-500">⚠️</span>
								{validation.warning}
							</div>
						)}

						{validation.isValid && (
							<div className="flex gap-2 items-center mt-2 text-sm text-green-600 dark:text-green-400">
								<span className="text-green-500">✅</span>
								Request body contains valid JSON
							</div>
						)}

						<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
							💡 Tip: You can paste JSON-like content (e.g.,{' '}
							<code className="px-1 bg-gray-100 rounded dark:bg-gray-700">
								{"{ title: 'foo', body: 'bar' }"}
							</code>
							) and click "Format JSON" to automatically add quotes and format it
							properly.
						</p>
					</div>

					{/* Available Variables */}
					{getAvailableTokens && Object.keys(getAvailableTokens()).length > 0 && (
						<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
							<h4 className="mb-3 font-medium text-gray-900 dark:text-white">
								🔗 Available Variables
							</h4>
							<p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
								Click the copy button to add these variables to your request body.
							</p>

							<div className="space-y-4">
								{(() => {
									const uniqueFields = getStandardizedVariables(
										nodeId,
										getNodes(),
										getEdges()
									);
									const groupedBySource = getGroupedVariables(
										nodeId,
										getNodes(),
										getEdges()
									);

									return Object.entries(groupedBySource).map(
										([source, fields]) => (
											<div key={source} className="space-y-2">
												<h5 className="pb-1 text-sm font-medium text-gray-700 capitalize border-b border-gray-200 dark:text-gray-300 dark:border-gray-600">
													{source === 'form'
														? '📝 Form Data'
														: source === 'execution'
														? '⚙️ Workflow Info'
														: source === 'user'
														? '👤 User Info'
														: `🔄 ${source}`}
												</h5>
												<div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
													{fields.map((field, fieldIndex) => (
														<div
															key={`${source}-${field.id}-${fieldIndex}`}
															className="flex justify-between items-center p-2 bg-gray-50 rounded border transition-colors dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 group"
														>
															<div className="flex-1 min-w-0">
																<code className="font-mono text-xs text-blue-600 break-all dark:text-blue-400">
																	{getVariableReference(field)}
																</code>
																{field.field.label &&
																	field.field.label !==
																		field.id && (
																		<p className="mt-1 text-xs text-gray-500 truncate dark:text-gray-400">
																			{field.field.label}
																		</p>
																	)}
															</div>
															<button
																onClick={() => {
																	copyToClipboard &&
																		copyToClipboard(
																			getVariableReference(
																				field
																			)
																		);
																}}
																className="p-1 ml-2 text-gray-400 opacity-0 transition-opacity hover:text-blue-600 dark:hover:text-blue-400 group-hover:opacity-100"
																title="Copy variable"
															>
																<Copy size={14} />
															</button>
														</div>
													))}
												</div>
											</div>
										)
									);
								})()}
							</div>
						</div>
					)}
				</>
			)}

			{config.method === 'GET' && (
				<div className="p-6 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
					<div className="flex gap-3 items-start">
						<div className="text-xl text-yellow-600 dark:text-yellow-400">ℹ️</div>
						<div>
							<h4 className="mb-2 font-medium text-yellow-900 dark:text-yellow-100">
								GET Request Note
							</h4>
							<p className="text-sm text-yellow-800 dark:text-yellow-200">
								GET requests don't typically include a request body. If you need to
								send parameters, add them as query parameters in Step 1, or use a
								different HTTP method like POST.
							</p>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

// Step 3: Response Mapping
function WizardStep3({
	config,
	nodeId,
	handleChange,
	addResponseField,
	removeResponseField,
	updateResponseField,
	saveFieldsFromTestResponse,
	testResult,
	getEffectiveStorageKey,
	generateDownstreamTokens,
	copyToClipboard,
}: WizardStepProps & {
	addResponseField: () => void;
	removeResponseField: (index: number) => void;
	updateResponseField: (index: number, field: 'key' | 'path' | 'type', value: string) => void;
	saveFieldsFromTestResponse: () => void;
	testResult: any;
	getEffectiveStorageKey: () => string;
	generateDownstreamTokens: () => Array<{ token: string; description: string }>;
}) {
	const [showTestResponse, setShowTestResponse] = useState(false);

	// Auto-generate field name from path
	const generateFieldName = (path: string) => {
		if (!path) return '';

		// Get the last segment of the path and convert to camelCase
		const segments = path.split('.');
		const lastSegment = segments[segments.length - 1];

		// Convert to camelCase (e.g., "user_name" -> "userName", "first-name" -> "firstName")
		return lastSegment
			.replace(/[_-](.)/g, (_, char) => char.toUpperCase())
			.replace(/^[A-Z]/, char => char.toLowerCase());
	};

	// Enhanced update function that auto-generates field names
	const handleFieldUpdate = (index: number, field: 'key' | 'path' | 'type', value: string) => {
		if (field === 'path') {
			// Auto-generate key when path changes
			const newKey = generateFieldName(value);
			const newFields = [...config.responseFields];
			newFields[index] = {
				...newFields[index],
				path: value,
				key: newKey || newFields[index].key, // Keep existing key if generation fails
			};
			handleChange({ responseFields: newFields });
		} else {
			updateResponseField(index, field, value);
		}
	};

	return (
		<div className="space-y-6 max-w-4xl">
			{/* Header */}
			<div className="p-6 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
				<div className="flex gap-3 items-start">
					<div className="text-2xl text-green-600 dark:text-green-400">📥</div>
					<div>
						<h3 className="mb-2 text-lg font-semibold text-green-900 dark:text-green-100">
							Extract Response Data
						</h3>
						<p className="text-sm text-green-800 dark:text-green-200">
							Choose which parts of the API response you want to use in other workflow
							steps. This step is optional - you can always access the full response
							later.
						</p>
					</div>
				</div>
			</div>

			{/* Test Response Viewer */}
			{testResult?.data && (
				<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
					<div className="flex justify-between items-center mb-3">
						<h4 className="font-medium text-gray-900 dark:text-white">
							📋 Test Response Data
						</h4>
						<button
							onClick={() => setShowTestResponse(!showTestResponse)}
							className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded border border-blue-200 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700 dark:hover:bg-blue-900/50"
						>
							{showTestResponse ? 'Hide Response' : 'View Response'}
						</button>
					</div>

					{showTestResponse && (
						<div className="mt-3">
							<p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
								💡 Use this JSON structure to help you build the correct paths
								below.
							</p>
							<div className="overflow-auto p-3 max-h-64 bg-gray-50 rounded border border-gray-200 dark:bg-gray-900 dark:border-gray-600">
								<pre className="text-xs text-gray-800 dark:text-gray-200">
									{JSON.stringify(testResult.data, null, 2)}
								</pre>
							</div>
						</div>
					)}
				</div>
			)}

			{/* Response Fields */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<div className="flex justify-between items-center mb-4">
					<div className="flex gap-2 items-center">
						<h4 className="font-medium text-gray-900 dark:text-white">
							🎯 Response Fields
						</h4>
						<div className="relative group">
							<button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
								<HelpCircle size={16} />
							</button>
							<div className="absolute bottom-full left-1/2 z-10 px-3 py-2 mb-2 w-64 text-xs text-white bg-gray-900 rounded-lg opacity-0 transition-opacity transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
								Define keys that will be available downstream like{' '}
								{`{{${getEffectiveStorageKey()}.userName}}`}. The path should match
								the API response JSON structure.
							</div>
						</div>
					</div>
					{testResult?.data && (
						<button
							onClick={saveFieldsFromTestResponse}
							className="px-3 py-1 text-sm text-green-700 bg-green-100 rounded border border-green-200 transition-colors hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-900/50"
						>
							Auto-Extract from Test
						</button>
					)}
				</div>

				{config.responseFields.length === 0 ? (
					<div className="py-8 text-center rounded-lg border-2 border-gray-300 border-dashed dark:border-gray-600">
						<div className="mb-3 text-4xl text-gray-400 dark:text-gray-500">📦</div>
						<p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
							No response fields configured
						</p>
						<p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
							Add fields to extract specific data from the API response for use in
							downstream steps.
						</p>
						<button
							onClick={addResponseField}
							className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
						>
							Add Response Field
						</button>
					</div>
				) : (
					<div className="space-y-4">
						{config.responseFields.map((field, index) => (
							<div
								key={index}
								className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700"
							>
								{/* Field Configuration */}
								<div className="grid grid-cols-1 gap-4 mb-3 lg:grid-cols-3">
									<div className="space-y-3 lg:col-span-2">
										<div>
											<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
												JSON Path <span className="text-red-500">*</span>
											</label>
											<input
												type="text"
												value={field.path}
												onChange={e =>
													handleFieldUpdate(index, 'path', e.target.value)
												}
												placeholder="results[0].matchCount"
												className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500 dark:focus:border-blue-400 ${
													!field.path || field.path.trim() === ''
														? 'border-red-300 dark:border-red-600'
														: isValidJsonPath(field.path)
														? 'border-gray-300 dark:border-gray-600'
														: 'border-yellow-300 dark:border-yellow-600'
												}`}
											/>
											<div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
												{field.path && field.path.trim() !== '' && (
													<div className="p-2 mb-2 bg-blue-50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
														<div className="mb-1 font-medium text-blue-800 dark:text-blue-200">
															Path Description:
														</div>
														<div className="text-blue-700 dark:text-blue-300">
															{describeJsonPath(field.path)}
														</div>
														{!isValidJsonPath(field.path) && (
															<div className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
																⚠️ Path syntax may not be valid.
																Check for typos in brackets or
																quotes.
															</div>
														)}
														{testResult?.data && (
															<div className="mt-1">
																<div className="text-xs text-blue-600 dark:text-blue-400">
																	Test Value:
																</div>
																<div className="px-2 py-1 mt-1 font-mono text-xs bg-white rounded border border-blue-200 dark:bg-gray-800 dark:border-blue-700">
																	{JSON.stringify(
																		getValueByPath(
																			testResult.data,
																			field.path
																		),
																		null,
																		2
																	) || 'undefined'}
																</div>
															</div>
														)}
													</div>
												)}
												<p className="mb-1">
													Path to the value in the response JSON.
													Supports:
												</p>
												<div className="space-y-1 font-mono text-xs">
													<div>
														•{' '}
														<span className="text-blue-600 dark:text-blue-400">
															data.user.name
														</span>{' '}
														- Object properties
													</div>
													<div>
														•{' '}
														<span className="text-green-600 dark:text-green-400">
															results[0].matchCount
														</span>{' '}
														- Array indexing
													</div>
													<div>
														•{' '}
														<span className="text-purple-600 dark:text-purple-400">
															sources[0].name
														</span>{' '}
														- Mixed notation
													</div>
													<div>
														•{' '}
														<span className="text-orange-600 dark:text-orange-400">
															$.results[0].value
														</span>{' '}
														- JSONPath syntax
													</div>
												</div>
											</div>
										</div>

										<div>
											<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
												Variable Name{' '}
												<span className="text-red-500">*</span>
											</label>
											<input
												type="text"
												value={field.key}
												onChange={e =>
													updateResponseField(
														index,
														'key',
														e.target.value
													)
												}
												placeholder="userName"
												className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-blue-500 dark:focus:border-blue-400 ${
													!field.key || field.key.trim() === ''
														? 'border-red-300 dark:border-red-600'
														: 'border-gray-300 dark:border-gray-600'
												}`}
											/>
											<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
												How this field will be referenced downstream
											</p>
										</div>
									</div>

									<div className="space-y-3">
										<div>
											<label className="block mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">
												Data Type
											</label>
											<select
												value={field.type}
												onChange={e =>
													updateResponseField(
														index,
														'type',
														e.target.value
													)
												}
												className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:border-blue-500 dark:focus:border-blue-400"
											>
												<option value="string">Text</option>
												<option value="number">Number</option>
												<option value="boolean">True/False</option>
												<option value="object">Object</option>
												<option value="array">List</option>
											</select>
										</div>

										<button
											onClick={() => removeResponseField(index)}
											className="px-3 py-2 w-full text-red-600 rounded transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
										>
											Remove Field
										</button>
									</div>
								</div>

								{/* Validation Warning */}
								{(!field.key ||
									field.key.trim() === '' ||
									!field.path ||
									field.path.trim() === '') && (
									<div className="p-2 mb-3 text-xs bg-yellow-50 rounded border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-700">
										<span className="text-yellow-800 dark:text-yellow-200">
											⚠️ This field is incomplete and won't be available
											downstream until both path and variable name are filled.
										</span>
									</div>
								)}

								{/* Mapping Visualization */}
								{field.path && field.key && field.key.trim() !== '' && (
									<div className="p-3 mt-3 bg-blue-50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
										<div className="space-y-1 text-xs">
											<div className="flex gap-2 items-center">
												<span className="text-gray-600 dark:text-gray-400">
													Mapping:
												</span>
												<code className="font-mono text-blue-600 dark:text-blue-400">
													"{field.key}"
												</code>
												<span className="text-gray-500">maps to →</span>
												<code className="font-mono text-green-600 dark:text-green-400">
													response.{field.path}
												</code>
											</div>
											<div className="flex gap-2 items-center">
												<span className="text-gray-600 dark:text-gray-400">
													Available as:
												</span>
												<code className="font-mono text-purple-600 dark:text-purple-400">{`{{${field.key}}}`}</code>
												<button
													onClick={() =>
														copyToClipboard &&
														copyToClipboard(`{{${field.key}}}`)
													}
													className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
													title="Copy variable"
												>
													<Copy size={12} />
												</button>
											</div>
										</div>
									</div>
								)}
							</div>
						))}

						<button
							onClick={addResponseField}
							className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg border border-blue-200 transition-colors hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700 dark:hover:bg-blue-900/50"
						>
							+ Add Another Field
						</button>
					</div>
				)}
			</div>

			{/* Preview */}
			{config.responseFields.length > 0 && (
				<div className="p-6 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
					<div className="flex gap-2 items-center mb-3">
						<h4 className="font-medium text-blue-900 dark:text-blue-100">
							🔮 Downstream Access Preview
						</h4>
						<div className="relative group">
							<button className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
								<HelpCircle size={16} />
							</button>
							<div className="absolute bottom-full left-1/2 z-10 px-3 py-2 mb-2 w-48 text-xs text-white bg-gray-900 rounded-lg opacity-0 transition-opacity transform -translate-x-1/2 pointer-events-none group-hover:opacity-100">
								These tokens will be available after this step executes. Click to
								copy.
							</div>
						</div>
					</div>
					<p className="mb-3 text-sm text-blue-800 dark:text-blue-200">
						Other workflow steps will be able to use these variables:
					</p>
					<div className="space-y-2">
						{generateDownstreamTokens().map((token, index) => (
							<div
								key={index}
								className="flex justify-between items-center p-2 bg-white rounded transition-colors dark:bg-blue-950 hover:bg-blue-50 dark:hover:bg-blue-900/30 group"
							>
								<code className="text-sm text-blue-600 dark:text-blue-400">
									{token.token}
								</code>
								<button
									onClick={() => copyToClipboard && copyToClipboard(token.token)}
									className="text-blue-600 opacity-0 transition-colors hover:text-blue-800 group-hover:opacity-100"
									title="Copy variable"
								>
									<Copy size={14} />
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

// Step 4: Review & Test
function WizardStep4({
	config,
	nodeId,
	handleChange,
	testApiRequest,
	isLoading,
	testResult,
	mockData,
	setMockData,
	isDark,
	getEffectiveStorageKey,
	generateDownstreamTokens,
	copyToClipboard,
}: WizardStepProps & {
	testApiRequest: () => void;
	isLoading: boolean;
	testResult: any;
	mockData: string;
	setMockData: (data: string) => void;
	isDark: boolean;
	getEffectiveStorageKey: () => string;
	generateDownstreamTokens: () => Array<{ token: string; description: string }>;
}) {
	return (
		<div className="space-y-6 max-w-4xl">
			{/* Header */}
			<div className="p-6 bg-orange-50 rounded-lg border border-orange-200 dark:bg-orange-900/20 dark:border-orange-800">
				<div className="flex gap-3 items-start">
					<div className="text-2xl text-orange-600 dark:text-orange-400">🧪</div>
					<div>
						<h3 className="mb-2 text-lg font-semibold text-orange-900 dark:text-orange-100">
							Review & Test
						</h3>
						<p className="text-sm text-orange-800 dark:text-orange-200">
							Review your configuration and test the API call to make sure everything
							works correctly.
						</p>
					</div>
				</div>
			</div>

			{/* Configuration Summary */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-4 font-medium text-gray-900 dark:text-white">
					📋 Configuration Summary
				</h4>
				<div className="space-y-3 text-sm">
					<div className="flex justify-between">
						<span className="text-gray-600 dark:text-gray-400">Endpoint:</span>
						<code className="text-blue-600 dark:text-blue-400">
							{config.method} {config.url}
						</code>
					</div>
					<div className="flex justify-between">
						<span className="text-gray-600 dark:text-gray-400">Authentication:</span>
						<span>
							{config.authentication.type === 'none'
								? 'None'
								: config.authentication.type}
						</span>
					</div>
					<div className="flex justify-between">
						<span className="text-gray-600 dark:text-gray-400">Storage Key:</span>
						<code className="text-green-600 dark:text-green-400">
							{getEffectiveStorageKey()}
						</code>
					</div>
					<div className="flex justify-between">
						<span className="text-gray-600 dark:text-gray-400">Response Fields:</span>
						<span>{config.responseFields.length} configured</span>
					</div>
				</div>
			</div>

			{/* Test Section */}
			<div className="p-6 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
				<h4 className="mb-4 font-medium text-gray-900 dark:text-white">🚀 Test API Call</h4>

				{config.method !== 'GET' && (
					<div className="mb-4">
						<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
							{config.requestSchema.template ||
							Object.keys(config.requestSchema).length > 0
								? 'Request Schema (JSON)'
								: 'Test Data (JSON)'}
						</label>
						{config.requestSchema.template && (
							<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
								Using request schema template. Variables like{' '}
								{'{{submission.field}}'} will be preserved for testing.
							</p>
						)}
						{Object.keys(config.requestSchema).length > 0 &&
							!config.requestSchema.template && (
								<p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
									Using generated mock data from request schema.
								</p>
							)}
						<Editor
							height="150px"
							defaultLanguage="json"
							value={mockData}
							onChange={value => setMockData(value || '')}
							options={{
								minimap: { enabled: false },
								lineNumbers: 'off',
								scrollBeyondLastLine: false,
								wordWrap: 'on',
								automaticLayout: true,
							}}
							theme={isDark ? 'vs-dark' : 'vs-light'}
						/>
					</div>
				)}

				<button
					onClick={testApiRequest}
					disabled={isLoading}
					className="flex gap-2 items-center px-6 py-3 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50"
				>
					{isLoading ? (
						<>
							<Loader2 className="animate-spin" size={16} />
							Testing...
						</>
					) : (
						<>
							<Play size={16} />
							Test API Call
						</>
					)}
				</button>

				{testResult && (
					<div
						className={`mt-4 p-4 rounded-lg ${
							testResult.success
								? 'bg-green-50 border border-green-200'
								: 'bg-red-50 border border-red-200'
						}`}
					>
						<div className="flex gap-2 items-center">
							{testResult.success ? (
								<CheckCircle className="text-green-600" size={20} />
							) : (
								<AlertCircle className="text-red-600" size={20} />
							)}
							<span className="font-medium">
								{testResult.success ? 'Test Successful!' : 'Test Failed'}
							</span>
						</div>
						{testResult.data && (
							<details className="mt-2">
								<summary className="text-sm text-gray-600 cursor-pointer dark:text-gray-400">
									View Response
								</summary>
								<pre className="overflow-auto p-2 mt-2 max-h-32 text-xs text-gray-800 bg-white rounded border border-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200">
									{JSON.stringify(testResult.data, null, 2)}
								</pre>
							</details>
						)}
					</div>
				)}
			</div>

			{/* Final Token Preview */}
			{generateDownstreamTokens().length > 0 && (
				<div className="p-6 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
					<h4 className="mb-3 font-medium text-green-900 dark:text-green-100">
						✨ Available Variables
					</h4>
					<p className="mb-3 text-sm text-green-800 dark:text-green-200">
						After this API call runs, these variables will be available in downstream
						workflow steps:
					</p>
					<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
						{generateDownstreamTokens().map((token, index) => (
							<div
								key={index}
								className="flex justify-between items-center p-2 bg-white rounded dark:bg-green-950"
							>
								<code className="text-sm text-green-600 dark:text-green-400">
									{token.token}
								</code>
								<button
									onClick={() => copyToClipboard && copyToClipboard(token.token)}
									className="text-green-600 transition-colors hover:text-green-800"
									title="Copy variable"
								>
									<Copy size={14} />
								</button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

export function ApiCallConfig({
	nodeId,
	data,
	onChange,
	onWizardStateChange,
	onValidationRequest,
}: ApiCallConfigProps) {
	const { getNodes, getEdges } = useReactFlow();
	const isDark = useIsDark();
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } =
		useNodeValidation();
	// Helper function to normalize headers from object to array format
	const normalizeHeaders = (headers: any): { key: string; value: string }[] => {
		if (!headers) return [];

		// If already in array format, return as is
		if (Array.isArray(headers)) {
			return headers;
		}

		// If in object format, convert to array
		if (typeof headers === 'object') {
			return Object.entries(headers).map(([key, value]) => ({
				key,
				value: String(value),
			}));
		}

		return [];
	};

	// Helper function to normalize body from object to JSON string format
	const normalizeBody = (body: any): string => {
		if (!body) return '';

		// If already a string, return as is
		if (typeof body === 'string') {
			return body;
		}

		// If it's an object, convert to pretty-printed JSON string
		if (typeof body === 'object') {
			return JSON.stringify(body, null, 2);
		}

		// For other types, convert to string
		return String(body);
	};

	const [config, setConfig] = useState<ApiCallConfig>(() => {
		const existingConfig = data.config as Partial<ApiCallConfig>;
		return {
			...defaultConfig,
			...existingConfig,
			authentication: {
				...defaultConfig.authentication,
				...(existingConfig?.authentication || {}),
			},
			headers: normalizeHeaders(existingConfig?.headers) || defaultConfig.headers,
			body: normalizeBody(existingConfig?.body) || defaultConfig.body,
			queryParams: existingConfig?.queryParams || defaultConfig.queryParams,
			requestSchema: existingConfig?.requestSchema || defaultConfig.requestSchema,
			responseSchema: existingConfig?.responseSchema || defaultConfig.responseSchema,
			responseFields: existingConfig?.responseFields || defaultConfig.responseFields,
		};
	});

	const [activeSection, setActiveSection] = useState<
		'basic' | 'request' | 'response' | 'responseFields' | 'test'
	>('basic');
	const [requestSchemaJson, setRequestSchemaJson] = useState('');
	const [responseSchemaJson, setResponseSchemaJson] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [testResult, setTestResult] = useState<TestResult | null>(null);
	const [mockData, setMockData] = useState('');
	const [showAvailableTokens, setShowAvailableTokens] = useState(false);
	const [showResponsePreview, setShowResponsePreview] = useState(false);
	const [wizardMode, setWizardMode] = useState(true); // Default to wizard mode for better UX
	const [currentStep, setCurrentStep] = useState(1);
	const [validationAttempted, setValidationAttempted] = useState(false); // Track if validation has been attempted
	const [validationFailed, setValidationFailed] = useState(false); // Track if validation failed on previous attempt

	// Get standardized variables using the utility
	const getStandardizedVariablesForNode = useCallback(() => {
		const nodes = getNodes();
		const edges = getEdges();
		return getStandardizedVariables(nodeId, nodes, edges);
	}, [nodeId, getNodes, getEdges]);

	// Wizard steps configuration
	const wizardSteps = [
		{
			id: 1,
			title: 'Configure Endpoint',
			description: 'Set up your API URL and basic settings',
		},
		{ id: 2, title: 'Request Payload', description: 'Define what data to send to the API' },
		{
			id: 3,
			title: 'Response Mapping',
			description: 'Extract useful data from the API response',
		},
		{
			id: 4,
			title: 'Review & Test',
			description: 'Review your configuration and test the API call',
		},
	];

	// Validation functions for each step
	const isStep1Valid = () => {
		return config.url.trim() !== '';
	};

	const isStep2Valid = () => {
		// Step 2 is optional for GET requests, required for others
		if (config.method === 'GET') return true;

		// For non-GET methods, validate that the request body contains valid JSON
		if (!config.body || !config.body.trim()) {
			return false; // Body is required for non-GET methods
		}

		try {
			// Try to parse as JSON first
			JSON.parse(config.body.trim());
			return true; // Valid JSON
		} catch (parseError) {
			// If it's not valid JSON, try to convert from object-like syntax
			try {
				// Replace unquoted property names with quoted ones
				const withQuotes = config.body
					.trim()
					.replace(/(\s*)(\w+)(\s*):(\s*)/g, '$1"$2"$3:$4');

				// Try to parse the modified content
				JSON.parse(withQuotes);
				return true; // Object-like syntax that can be converted
			} catch (secondError) {
				// If still can't parse, try to evaluate as a JavaScript object
				try {
					// Create a function that returns the object
					const func = new Function(`return ${config.body.trim()}`);
					const parsed = func();

					// Validate that it's actually an object
					if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
						return true; // Valid object-like syntax
					}

					return false; // Not a valid object
				} catch (thirdError) {
					return false; // Invalid syntax
				}
			}
		}
	};

	const isStep3Valid = () => {
		return true; // Response mapping is optional
	};

	const canProceedToStep = (step: number) => {
		switch (step) {
			case 2:
				return isStep1Valid();
			case 3:
				return isStep1Valid() && isStep2Valid();
			case 4:
				return isStep1Valid() && isStep2Valid() && isStep3Valid();
			default:
				return true;
		}
	};

	// Communicate wizard state to parent component
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: wizardSteps.length,
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(currentStep + 1),
				validationAttempted,
				validationFailed,
				onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed

					// Save the current configuration
					const finalConfig = {
						...data,
						config: {
							...config,
							// Clean up any invalid headers before saving
							headers: config.headers.filter(
								header =>
									header.key &&
									header.key.trim() !== '' &&
									header.value &&
									header.value.trim() !== ''
							),
							// Clean up any invalid response fields before saving
							responseFields: config.responseFields.filter(
								field =>
									field.key &&
									field.key.trim() !== '' &&
									field.path &&
									field.path.trim() !== ''
							),
						},
					};
					onChange(finalConfig);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);

					// Exit wizard mode
					setWizardMode(false);
				},
			});
		}
	}, [wizardMode, currentStep, config, data, onChange, onWizardStateChange]);

	// Get available context fields for token insertion
	const getAvailableTokens = () => {
		const nodes = getNodes();
		const edges = getEdges();
		const contexts = getAvailableFields(nodeId, nodes, edges);

		// Flatten and group fields by source
		const grouped: Record<string, ContextField[]> = {};

		// Track used field names to prevent duplicates
		const usedFieldNames = new Set<string>();

		// Process contexts from upstream nodes
		for (const context of contexts) {
			for (const field of context.fields) {
				// For start nodes (start.webhook, start.form, start.schedule),
				// don't prepend the node ID since their data becomes the root context
				const isStartNode = context.nodeType.startsWith('start.');
				const fullFieldId = isStartNode ? field.id : `${context.nodeId}.${field.id}`;
				const [source] = fullFieldId.split('.');
				if (!grouped[source]) {
					grouped[source] = [];
				}

				// Check if field with same ID already exists to prevent duplicates
				const existingField = grouped[source].find(
					existingField => existingField.id === fullFieldId
				);
				if (!existingField) {
					// Get the clean field name (without source prefix)
					let cleanFieldName = field.id.includes('.')
						? field.id.split('.').pop() || field.id
						: field.id;

					// If the clean field name is already used, make it unique
					if (usedFieldNames.has(cleanFieldName)) {
						// Add source prefix to make it unique
						const sourcePrefix =
							source === 'form' ? 'form' : source === 'execution' ? 'exec' : source;
						cleanFieldName = `${sourcePrefix}_${cleanFieldName}`;
					}

					// Mark this field name as used
					usedFieldNames.add(cleanFieldName);

					grouped[source].push({
						...field,
						id: fullFieldId,
						label: isStartNode ? field.label : `${context.nodeLabel} → ${field.label}`,
						description: isStartNode
							? field.description
							: `${field.description} (from ${context.nodeLabel})`,
					});
				}
			}
		}

		// Add static context fields
		grouped['execution'] = [
			{
				id: 'execution.workflowId',
				label: 'Workflow ID',
				type: 'string',
				description: 'Current workflow ID',
			},
			{
				id: 'execution.executionId',
				label: 'Execution ID',
				type: 'string',
				description: 'Current execution ID',
			},
			{
				id: 'execution.timestamp',
				label: 'Execution Timestamp',
				type: 'string',
				description: 'When this execution started',
			},
		];

		grouped['user'] = [
			{
				id: 'user.email',
				label: 'User Email',
				type: 'string',
				description: 'Email of the user who triggered this workflow',
			},
			{
				id: 'user.id',
				label: 'User ID',
				type: 'string',
				description: 'ID of the user who triggered this workflow',
			},
		];

		return grouped;
	};

	// Generate the storage key that will be used for this node's response
	const getEffectiveStorageKey = () => {
		return config.storeAs?.trim() || nodeId;
	};

	// Generate preview of how downstream nodes can access this API's response
	const generateDownstreamTokens = () => {
		const storageKey = getEffectiveStorageKey();
		const commonTokens = [
			{
				token: `{{${storageKey}.statusCode}}`,
				description: 'HTTP status code from response',
			},
			{ token: `{{${storageKey}.headers}}`, description: 'Response headers object' },
			{ token: `{{${storageKey}.data}}`, description: 'Full response body' },
		];

		// Filter out response fields with empty or invalid keys
		const responseFieldTokens = config.responseFields
			.filter(field => field.key && field.key.trim() !== '') // Only include fields with valid keys
			.map(field => ({
				token: `{{${field.key}}}`,
				description: `${field.type} value extracted from ${field.path}`,
			}));

		return [...responseFieldTokens, ...commonTokens];
	};

	// Initialize JSON strings from schema objects
	useEffect(() => {
		// Handle request schema - could be template or old format
		if (config.requestSchema.template) {
			// New format with template - format it nicely
			const formattedTemplate = formatJsonTemplate(config.requestSchema.template);
			setRequestSchemaJson(formattedTemplate);
		} else if (Object.keys(config.requestSchema).length > 0) {
			// Old format - convert to template
			const fields = Object.entries(config.requestSchema).map(([key, value]) => {
				if (typeof value === 'string' && value.startsWith('{{') && value.endsWith('}}')) {
					return `"${key}": ${value}`;
				}
				return `"${key}": "{{${key}}}"`;
			});
			const template = `{${fields.join(', ')}}`;
			const formattedTemplate = formatJsonTemplate(template);
			setRequestSchemaJson(formattedTemplate);
			handleChange({ requestSchema: { template } });
		}

		if (Object.keys(config.responseSchema).length > 0) {
			setResponseSchemaJson(JSON.stringify(config.responseSchema, null, 2));
		}
	}, []);

	// Generate mock data when request schema changes
	useEffect(() => {
		if (config.requestSchema.template) {
			// Use the request schema template directly for testing
			setMockData(config.requestSchema.template);
		} else if (Object.keys(config.requestSchema).length > 0) {
			// Legacy format - generate mock data from schema
			const mock = generateMockData(config.requestSchema);
			setMockData(JSON.stringify(mock, null, 2));
		} else {
			// No schema, create basic example
			const basicMock = {
				example: 'value',
				number: 42,
				boolean: true,
			};
			setMockData(JSON.stringify(basicMock, null, 2));
		}
	}, [config.requestSchema]);

	// Get upstream submission form fields to prepopulate request schema
	const getUpstreamFormFields = () => {
		const nodes = getNodes();
		const edges = getEdges();
		const availableFields = getAvailableFields(nodeId, nodes, edges);

		// Find submission form fields
		const submissionFormContext = availableFields.find(
			context => context.nodeType === 'submission-form'
		);

		return submissionFormContext?.fields || [];
	};

	// Generate mock data based on schema
	const generateMockData = (schema: Record<string, string>) => {
		const mock: any = {};
		Object.entries(schema).forEach(([key, type]) => {
			switch (type) {
				case 'string':
					mock[key] = key.toLowerCase().includes('email')
						? 'test@example.com'
						: key.toLowerCase().includes('name')
						? 'John Doe'
						: key.toLowerCase().includes('company')
						? 'Acme Corp'
						: 'Sample text';
					break;
				case 'number':
					mock[key] = key.toLowerCase().includes('amount')
						? 1500.0
						: key.toLowerCase().includes('age')
						? 35
						: key.toLowerCase().includes('count')
						? 3
						: 42;
					break;
				case 'boolean':
					mock[key] = true;
					break;
				case 'array':
					mock[key] = ['item1', 'item2'];
					break;
				case 'object':
					mock[key] = { property: 'value' };
					break;
				default:
					mock[key] = 'sample value';
			}
		});
		return mock;
	};

	// Format JSON template with proper indentation
	const formatJsonTemplate = (template: string) => {
		try {
			// Replace variables with placeholder values for formatting
			const placeholderTemplate = template.replace(/\{\{[^}]+\}\}/g, '"VARIABLE"');
			const parsed = JSON.parse(placeholderTemplate);
			const formatted = JSON.stringify(parsed, null, 2);
			// Replace placeholders back with variables
			const variables = template.match(/\{\{[^}]+\}\}/g) || [];
			let result = formatted;
			variables.forEach(variable => {
				result = result.replace('"VARIABLE"', variable);
			});
			return result;
		} catch (error) {
			// If formatting fails, return the original template
			return template;
		}
	};

	// Auto-populate request schema based on upstream submission form
	const generateRequestSchema = () => {
		const formFields = getUpstreamFormFields();

		if (formFields.length === 0) {
			// Try to use available tokens instead
			const availableTokens = getAvailableTokens();
			const allFields = Object.values(availableTokens).flat();

			if (allFields.length > 0) {
				const bodyObject: Record<string, string> = {};

				// Use available fields to create the body
				allFields.slice(0, 8).forEach(field => {
					// Limit to first 8 fields
					const fieldKey = field.id.includes('.')
						? field.id.split('.').pop() || field.id
						: field.id;
					bodyObject[fieldKey] = `{{${field.id}}}`;
				});

				const bodyString = JSON.stringify(bodyObject, null, 2);
				handleChange({ body: bodyString });
				return;
			}

			// No upstream data, create example schema
			const exampleBody = {
				firstName: '{{form.firstName}}',
				lastName: '{{form.lastName}}',
				email: '{{form.email}}',
				amount: '{{form.amount}}',
				timestamp: '{{execution.timestamp}}',
			};

			const bodyString = JSON.stringify(exampleBody, null, 2);
			handleChange({ body: bodyString });
			return;
		}

		// Create body from form fields with proper variable syntax
		const bodyObject: Record<string, string> = {};
		formFields.forEach(field => {
			// Use the field ID without node prefix for cleaner body
			const fieldKey = field.id.includes('.') ? field.id.split('.').pop() : field.id;
			// Use the full field ID for the variable reference
			bodyObject[fieldKey || field.id] = `{{${field.id}}}`;
		});

		const bodyString = JSON.stringify(bodyObject, null, 2);
		handleChange({ body: bodyString });
	};

	// Validation is now only triggered by parent components (save/exit)
	// No automatic validation on every change to avoid excessive API calls

	const handleChange = (newConfig: Partial<ApiCallConfig>) => {
		const updatedConfig = { ...config, ...newConfig };

		// Debug logging for headers
		if (newConfig.headers !== undefined) {
			console.log('🔧 Frontend - Headers changed:', {
				newHeaders: newConfig.headers,
				updatedHeaders: updatedConfig.headers,
				headerCount: updatedConfig.headers.length,
			});
		}

		setConfig(updatedConfig);
		const updatedData = {
			...data,
			config: updatedConfig,
		};
		onChange(updatedData);

		// Note: Validation is now only triggered by parent components (save/exit)
		// No automatic validation on every change to avoid excessive API calls
	};

	const handleAuthChange = (authChanges: Partial<ApiCallConfig['authentication']>) => {
		handleChange({
			authentication: {
				...config.authentication,
				...authChanges,
			},
		});
	};

	const addHeader = () => {
		const newHeaders = [...config.headers, { key: '', value: '' }];
		console.log('🔧 Frontend - Adding header:', {
			currentHeaders: config.headers,
			newHeaders,
			newHeaderCount: newHeaders.length,
		});
		handleChange({
			headers: newHeaders,
		});
	};

	const removeHeader = (index: number) => {
		const newHeaders = [...config.headers];
		newHeaders.splice(index, 1);
		handleChange({ headers: newHeaders });
	};

	const addQueryParam = () => {
		handleChange({
			queryParams: [...config.queryParams, { key: '', value: '' }],
		});
	};

	const removeQueryParam = (index: number) => {
		const newParams = [...config.queryParams];
		newParams.splice(index, 1);
		handleChange({ queryParams: newParams });
	};

	const handleRequestSchemaChange = (value: string | undefined) => {
		setRequestSchemaJson(value || '');
		if (value) {
			// Save as template string instead of trying to parse as JSON
			handleChange({ requestSchema: { template: value } });
		}
	};

	const handleResponseSchemaChange = (value: string | undefined) => {
		setResponseSchemaJson(value || '');
		try {
			if (value) {
				const parsed = JSON.parse(value);
				handleChange({ responseSchema: parsed });
			}
		} catch (error) {
			// Invalid JSON, don't update schema
		}
	};

	const generateResponseSchema = () => {
		const exampleSchema = {
			quoteAmount: 'number',
			quoteId: 'string',
			status: 'string',
			expiresAt: 'string',
		};
		setResponseSchemaJson(JSON.stringify(exampleSchema, null, 2));
		handleChange({ responseSchema: exampleSchema });
	};

	// Helper functions for response fields
	const addResponseField = () => {
		handleChange({
			responseFields: [...config.responseFields, { key: '', path: '', type: 'string' }],
		});
	};

	const removeResponseField = (index: number) => {
		const newFields = [...config.responseFields];
		newFields.splice(index, 1);
		handleChange({ responseFields: newFields });
	};

	const updateResponseField = (index: number, field: 'key' | 'path' | 'type', value: string) => {
		const newFields = [...config.responseFields];
		newFields[index] = { ...newFields[index], [field]: value };
		handleChange({ responseFields: newFields });
	};

	// Clean up headers with empty keys or values
	const cleanupHeaders = () => {
		const validHeaders = config.headers.filter(
			header =>
				header.key && header.key.trim() !== '' && header.value && header.value.trim() !== ''
		);

		if (validHeaders.length !== config.headers.length) {
			handleChange({ headers: validHeaders });
		}
	};

	// Clean up response fields with empty keys
	const cleanupResponseFields = () => {
		const validFields = config.responseFields.filter(
			field => field.key && field.key.trim() !== '' && field.path && field.path.trim() !== ''
		);

		if (validFields.length !== config.responseFields.length) {
			handleChange({ responseFields: validFields });
		}
	};

	// Clean up invalid headers and response fields on component mount
	useEffect(() => {
		cleanupHeaders();
		cleanupResponseFields();
	}, []); // Only run once on mount

	// Extract fields from test response
	const extractFieldsFromResponse = (responseData: any) => {
		const fields: { key: string; path: string; type: string }[] = [];

		const extractFromObject = (obj: any, path: string = '') => {
			if (!obj || typeof obj !== 'object') return;

			if (Array.isArray(obj)) {
				// Handle arrays - extract from first element to suggest structure
				if (obj.length > 0) {
					const firstElement = obj[0];
					const arrayPath = path ? `${path}[0]` : '[0]';

					if (typeof firstElement === 'object' && firstElement !== null) {
						// If array contains objects, recurse into first object
						extractFromObject(firstElement, arrayPath);
					} else {
						// If array contains primitives, add the array access path
						const type = Array.isArray(firstElement) ? 'array' : typeof firstElement;
						const key = path ? `${path.split('.').pop() || 'item'}At0` : 'item0';
						fields.push({
							key: key,
							path: arrayPath,
							type: type,
						});
					}
				}

				// Also add a reference to the full array
				if (path) {
					const arrayKey = path.split('.').pop() || 'array';
					fields.push({
						key: arrayKey,
						path: path,
						type: 'array',
					});
				}
			} else {
				// Handle regular objects
				Object.entries(obj).forEach(([key, value]) => {
					const currentPath = path ? `${path}.${key}` : key;

					if (Array.isArray(value)) {
						// Array field
						fields.push({
							key: key,
							path: currentPath,
							type: 'array',
						});

						// Also extract from first array element if it exists
						if (value.length > 0) {
							const firstElement = value[0];
							if (typeof firstElement === 'object' && firstElement !== null) {
								extractFromObject(firstElement, `${currentPath}[0]`);
							} else {
								// For primitive arrays, add first element access
								const elementType = typeof firstElement;
								fields.push({
									key: `${key}FirstItem`,
									path: `${currentPath}[0]`,
									type: elementType,
								});
							}
						}
					} else if (typeof value === 'object' && value !== null) {
						// Nested object - add the object itself and recurse
						fields.push({
							key: key,
							path: currentPath,
							type: 'object',
						});
						extractFromObject(value, currentPath);
					} else {
						// Primitive value
						const type = typeof value;
						fields.push({
							key: key,
							path: currentPath,
							type: type,
						});
					}
				});
			}
		};

		extractFromObject(responseData);

		// Remove duplicates and sort by path length (simpler paths first)
		const uniqueFields = fields.filter(
			(field, index, array) => array.findIndex(f => f.path === field.path) === index
		);

		return uniqueFields.sort((a, b) => a.path.length - b.path.length);
	};

	// Save fields from test response
	const saveFieldsFromTestResponse = () => {
		if (testResult?.data) {
			const extractedFields = extractFieldsFromResponse(testResult.data);
			handleChange({ responseFields: extractedFields });
		}
	};

	/**
	 * Validate URL format with comprehensive checks
	 */
	const validateUrlFormat = (
		url: string
	): { isValid: boolean; error?: string; warning?: string } => {
		if (!url || url.trim() === '') {
			return { isValid: false, error: 'URL is required' };
		}

		const trimmedUrl = url.trim();

		// Check for whitespace issues
		if (trimmedUrl !== url) {
			return {
				isValid: false,
				warning: 'URL should not have leading or trailing whitespace',
			};
		}

		try {
			const urlObj = new URL(trimmedUrl);

			// Check protocol
			if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
				return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' };
			}

			// Check hostname
			if (!urlObj.hostname || urlObj.hostname.length === 0) {
				return { isValid: false, error: 'URL must have a valid hostname' };
			}

			// Check for valid hostname format
			const hostnameRegex =
				/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
			if (!hostnameRegex.test(urlObj.hostname)) {
				return { isValid: false, error: 'URL has an invalid hostname format' };
			}

			// Check for localhost or private IP addresses (security warning)
			const isLocalhost =
				urlObj.hostname === 'localhost' ||
				urlObj.hostname === '127.0.0.1' ||
				urlObj.hostname === '::1' ||
				urlObj.hostname.startsWith('192.168.') ||
				urlObj.hostname.startsWith('10.') ||
				urlObj.hostname.startsWith('172.') ||
				urlObj.hostname.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\./);

			if (isLocalhost) {
				return {
					isValid: true,
					warning:
						'URL points to localhost/private IP - may not be accessible in production',
				};
			}

			// Check for common security issues
			if (urlObj.hostname.includes('..') || urlObj.hostname.includes('--')) {
				return {
					isValid: false,
					error: 'URL contains potentially malicious hostname patterns',
				};
			}

			// Check port if specified
			if (urlObj.port) {
				const port = parseInt(urlObj.port, 10);
				if (isNaN(port) || port < 1 || port > 65535) {
					return { isValid: false, error: 'URL has an invalid port number' };
				}
			}

			// Check for extremely long URLs
			if (trimmedUrl.length > 2048) {
				return { isValid: false, error: 'URL is too long (maximum 2048 characters)' };
			}

			return { isValid: true };
		} catch (error) {
			return { isValid: false, error: 'URL must be a valid format' };
		}
	};

	const isConfigValid = () => {
		const urlValidation = validateUrlFormat(config.url);
		return urlValidation.isValid;
	};

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			const result = await validateNode(nodeId, data);

			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('Validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Copy token to clipboard
	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	// Test the API request
	const testApiRequest = async () => {
		if (!isConfigValid()) {
			return;
		}

		setIsLoading(true);
		setTestResult(null);
		const startTime = Date.now();

		try {
			// Parse mock data
			let requestBody: any = null;
			if (config.method !== 'GET') {
				if (mockData && mockData.trim()) {
					try {
						// Check if mockData is a template with variables
						if (mockData.includes('{{') && mockData.includes('}}')) {
							// For templates with variables, use as-is for testing (variables will be preserved)
							requestBody = mockData;
						} else {
							// Regular JSON data
							requestBody = JSON.parse(mockData);
						}
					} catch (error) {
						throw new Error('Invalid JSON in mock data');
					}
				} else if (config.body && config.body.trim()) {
					// Use the body template if no mock data
					requestBody = config.body;
				}
			}

			// Build headers
			const headers: Record<string, string> = {
				'Content-Type': 'application/json',
			};

			// Add configured headers
			config.headers.forEach(header => {
				if (header.key && header.value) {
					headers[header.key] = header.value;
				}
			});

			// Add authentication
			if (
				config.authentication.type === 'basic' &&
				config.authentication.username &&
				config.authentication.password
			) {
				const credentials = btoa(
					`${config.authentication.username}:${config.authentication.password}`
				);
				headers['Authorization'] = `Basic ${credentials}`;
			} else if (config.authentication.type === 'bearer' && config.authentication.token) {
				headers['Authorization'] = `Bearer ${config.authentication.token}`;
			}

			// Build URL with query params
			let url = config.url;
			if (config.queryParams.length > 0) {
				const params = new URLSearchParams();
				config.queryParams.forEach(param => {
					if (param.key && param.value) {
						params.append(param.key, param.value);
					}
				});
				url += (url.includes('?') ? '&' : '?') + params.toString();
			}

			// Make the request
			const response = await fetch(url, {
				method: config.method,
				headers,
				body: requestBody ? JSON.stringify(requestBody) : undefined,
			});

			const duration = Date.now() - startTime;
			let responseData: any;

			try {
				responseData = await response.json();
			} catch {
				responseData = await response.text();
			}

			setTestResult({
				success: response.ok,
				status: response.status,
				statusText: response.statusText,
				data: responseData,
				duration,
				timestamp: new Date().toISOString(),
			});
		} catch (error) {
			const duration = Date.now() - startTime;
			setTestResult({
				success: false,
				error: error instanceof Error ? error.message : 'Unknown error occurred',
				duration,
				timestamp: new Date().toISOString(),
			});
		} finally {
			setIsLoading(false);
		}
	};

	const sections = [
		{ id: 'basic' as const, label: 'Basic Config' },
		{ id: 'request' as const, label: 'Request Payload' },
		{ id: 'response' as const, label: 'Response Schema' },
		{ id: 'responseFields' as const, label: 'Response Fields' },
		{ id: 'test' as const, label: 'Test Request', disabled: !isConfigValid() },
	];

	// Communicate wizard state changes to parent
	useEffect(() => {
		if (onWizardStateChange) {
			onWizardStateChange({
				wizardMode,
				currentStep,
				totalSteps: wizardSteps.length,
				canProceedToNext: canProceedToStep(currentStep + 1),
				onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
				onNextStep: () => setCurrentStep(currentStep + 1),
				validationAttempted,
				validationFailed,
				onCompleteWizard: async () => {
					// Step 1: If validation hasn't been attempted yet, run validation and stay in node
					if (!validationAttempted) {
						try {
							const result = await validateNode(nodeId, data);
							setValidationAttempted(true);

							if (!result.valid) {
								// Validation failed - stay in node and show errors
								setValidationFailed(true);
								console.log('Validation failed, staying in node:', result);
								return; // Don't exit the node
							} else {
								// Validation succeeded - proceed to exit
								setValidationFailed(false);
								console.log('Validation succeeded:', result);
							}
						} catch (error) {
							console.error('Validation failed:', error);
							setValidationAttempted(true);
							setValidationFailed(true);
							return; // Don't exit the node on error
						}
					}

					// Step 2: If validation was already attempted (regardless of success/failure), exit the node
					// This allows users to force exit even if validation failed

					// Save the current configuration
					const finalConfig = {
						...data,
						config: {
							...config,
							// Clean up any invalid headers before saving
							headers: config.headers.filter(
								header =>
									header.key &&
									header.key.trim() !== '' &&
									header.value &&
									header.value.trim() !== ''
							),
							// Clean up any invalid response fields before saving
							responseFields: config.responseFields.filter(
								field =>
									field.key &&
									field.key.trim() !== '' &&
									field.path &&
									field.path.trim() !== ''
							),
						},
					};
					onChange(finalConfig);

					// Reset validation state for next time
					setValidationAttempted(false);
					setValidationFailed(false);

					// Exit wizard mode
					setWizardMode(false);
				},
			});
		}
	}, [wizardMode, currentStep, config, data, onChange, onWizardStateChange]);

	return (
		<div className="flex flex-col h-full">
			{/* Validation Results */}
			{(isValidating || lastValidationResult) && (
				<div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
					<ValidationResultDisplay
						errors={lastValidationResult?.errors || []}
						isValidating={isValidating}
						onDismiss={clearValidationResult}
					/>
				</div>
			)}

			{/* Mode Toggle */}
			<div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-900">
				<div className="flex justify-between items-center">
					<h2 className="text-lg font-semibold text-gray-900 dark:text-white">
						API Call Configuration
					</h2>
					<div className="flex gap-3 items-center">
						<span className="text-sm text-gray-600 dark:text-gray-400">
							{wizardMode ? '🧙‍♂️ Wizard Mode' : '⚙️ Advanced Mode'}
						</span>
						<button
							onClick={() => {
								const newWizardMode = !wizardMode;
								setWizardMode(newWizardMode);
								if (newWizardMode) setCurrentStep(1); // Reset to step 1 when entering wizard mode
							}}
							className="inline-flex relative items-center w-11 h-6 bg-gray-200 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-gray-700"
						>
							<span
								className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
									wizardMode ? 'translate-x-6' : 'translate-x-1'
								}`}
							/>
						</button>
					</div>
				</div>
			</div>

			{wizardMode ? (
				// Wizard Mode Interface
				<div className="flex flex-col h-full">
					{/* Progress Bar */}
					<div className="px-6 py-4 bg-gray-50 border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
						<div className="flex justify-between items-center mb-2">
							<h3 className="text-sm font-medium text-gray-900 dark:text-white">
								Step {currentStep} of {wizardSteps.length}:{' '}
								{wizardSteps[currentStep - 1]?.title}
							</h3>
							<span className="text-xs text-gray-500 dark:text-gray-400">
								{Math.round((currentStep / wizardSteps.length) * 100)}% Complete
							</span>
						</div>
						<p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
							{wizardSteps[currentStep - 1]?.description}
						</p>

						{/* Progress Bar */}
						<div className="w-full h-2 bg-gray-200 rounded-full dark:bg-gray-700">
							<div
								className="h-2 bg-blue-600 rounded-full transition-all duration-300"
								style={{ width: `${(currentStep / wizardSteps.length) * 100}%` }}
							/>
						</div>

						{/* Step Indicators */}
						<div className="flex justify-between mt-3">
							{wizardSteps.map(step => (
								<button
									key={step.id}
									onClick={() => {
										if (canProceedToStep(step.id)) {
											setCurrentStep(step.id);
										}
									}}
									disabled={!canProceedToStep(step.id)}
									className={`flex items-center text-xs font-medium transition-colors ${
										step.id === currentStep
											? 'text-blue-600 dark:text-blue-400'
											: step.id < currentStep
											? 'text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300'
											: canProceedToStep(step.id)
											? 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
											: 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
									}`}
								>
									<div
										className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-2 ${
											step.id === currentStep
												? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
												: step.id < currentStep
												? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
												: canProceedToStep(step.id)
												? 'border-gray-300 dark:border-gray-600'
												: 'border-gray-200 dark:border-gray-700'
										}`}
									>
										{step.id < currentStep ? (
											<CheckCircle
												size={12}
												className="text-green-600 dark:text-green-400"
											/>
										) : (
											<span className="text-xs">{step.id}</span>
										)}
									</div>
									<span className="hidden sm:inline">{step.title}</span>
								</button>
							))}
						</div>
					</div>

					{/* Wizard Content */}
					<div className="flex flex-col flex-1 min-h-0">
						<div className="overflow-auto flex-1 p-6 bg-white dark:bg-gray-900">
							{currentStep === 1 && (
								<WizardStep1
									config={config}
									nodeId={nodeId}
									handleChange={handleChange}
									handleAuthChange={handleAuthChange}
									addHeader={addHeader}
									removeHeader={removeHeader}
									addQueryParam={addQueryParam}
									removeQueryParam={removeQueryParam}
									getAvailableTokens={getAvailableTokens}
									copyToClipboard={copyToClipboard}
								/>
							)}

							{currentStep === 2 && (
								<WizardStep2
									config={config}
									nodeId={nodeId}
									handleChange={handleChange}
									generateRequestSchema={generateRequestSchema}
									getAvailableTokens={getAvailableTokens}
									copyToClipboard={copyToClipboard}
									getNodes={getNodes}
									getEdges={getEdges}
								/>
							)}

							{currentStep === 3 && (
								<WizardStep3
									config={config}
									nodeId={nodeId}
									handleChange={handleChange}
									addResponseField={addResponseField}
									removeResponseField={removeResponseField}
									updateResponseField={updateResponseField}
									saveFieldsFromTestResponse={saveFieldsFromTestResponse}
									testResult={testResult}
									getEffectiveStorageKey={getEffectiveStorageKey}
									generateDownstreamTokens={generateDownstreamTokens}
									copyToClipboard={copyToClipboard}
								/>
							)}

							{currentStep === 4 && (
								<WizardStep4
									config={config}
									nodeId={nodeId}
									handleChange={handleChange}
									testApiRequest={testApiRequest}
									isLoading={isLoading}
									testResult={testResult}
									mockData={mockData}
									setMockData={setMockData}
									isDark={isDark}
									getEffectiveStorageKey={getEffectiveStorageKey}
									generateDownstreamTokens={generateDownstreamTokens}
									copyToClipboard={copyToClipboard}
								/>
							)}
						</div>
					</div>
				</div>
			) : (
				// Advanced Mode Interface (existing code)
				<>
					{/* Section Tabs */}
					<div className="flex bg-gray-50 border-b border-gray-200 transition-colors dark:border-gray-700 dark:bg-gray-800">
						{sections.map(section => (
							<button
								key={section.id}
								disabled={section.disabled}
								className={`px-4 py-2 font-medium text-sm transition-colors ${
									activeSection === section.id
										? 'border-b-2 border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-700'
										: section.disabled
										? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
										: 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
								}`}
								onClick={() => !section.disabled && setActiveSection(section.id)}
							>
								{section.label}
								{section.disabled && <span className="ml-1 text-xs">⚠️</span>}
							</button>
						))}
					</div>

					{/* Available Tokens Panel - Always visible */}
					<div className="bg-gray-50 border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
						<button
							onClick={() => setShowAvailableTokens(!showAvailableTokens)}
							className="flex justify-between items-center px-6 py-3 w-full text-sm font-medium text-gray-700 transition-colors dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
						>
							<div className="flex gap-2 items-center">
								{showAvailableTokens ? (
									<ChevronDown size={16} />
								) : (
									<ChevronRight size={16} />
								)}
								<span>Available Variables</span>
								<span className="text-xs text-gray-500 dark:text-gray-400">
									({Object.values(getAvailableTokens()).flat().length} available)
								</span>
							</div>
							{showAvailableTokens ? <EyeOff size={16} /> : <Eye size={16} />}
						</button>

						{showAvailableTokens && (
							<div className="px-6 pb-4">
								{/* Help Text */}
								<div className="p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
									<div className="flex gap-2 items-start">
										<div className="text-blue-600 dark:text-blue-400 mt-0.5">
											💡
										</div>
										<div className="text-sm">
											<p className="mb-1 font-medium text-blue-900 dark:text-blue-100">
												How to use variables:
											</p>
											<ul className="space-y-1 text-xs text-blue-800 dark:text-blue-200">
												<li>
													• Click any variable below to copy it to your
													clipboard
												</li>
												<li>
													• Paste variables into URL, headers, body, or
													any text field
												</li>
												<li>
													• Variables are replaced with actual data when
													the API call executes
												</li>
												<li>
													• Example:{' '}
													<code className="px-1 bg-blue-100 rounded dark:bg-blue-800">
														https://api.example.com/users/
														{'{{form.userId}}'}
													</code>
												</li>
											</ul>
										</div>
									</div>
								</div>

								<div className="overflow-y-auto max-h-48">
									<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
										{(() => {
											const uniqueFields = getStandardizedVariablesForNode();
											const groupedBySource = getGroupedVariables(
												nodeId,
												getNodes(),
												getEdges()
											);

											return Object.entries(groupedBySource).map(
												([source, fields]) => (
													<div
														key={source}
														className="p-3 bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-600"
													>
														<h4 className="mb-2 text-sm font-medium text-gray-900 capitalize dark:text-gray-100">
															{source === 'form'
																? '📝 Form Data'
																: source === 'execution'
																? '⚙️ Workflow Info'
																: source === 'user'
																? '👤 User Info'
																: `🔄 ${source}`}
														</h4>
														<div className="space-y-1">
															{fields.slice(0, 3).map(field => (
																<div
																	key={field.id}
																	className="flex justify-between items-center text-xs"
																>
																	<span className="font-mono text-gray-600 dark:text-gray-400">
																		{getVariableDisplayText(
																			field
																		)}
																	</span>
																	<button
																		onClick={() => {
																			copyToClipboard(
																				getVariableDisplayText(
																					field
																				)
																			);
																		}}
																		className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
																		title="Copy variable"
																	>
																		<Copy size={12} />
																	</button>
																</div>
															))}
															{fields.length > 3 && (
																<div className="text-xs text-gray-500 dark:text-gray-400">
																	+{fields.length - 3} more
																	variables
																</div>
															)}
														</div>
													</div>
												)
											);
										})()}
									</div>

									{getStandardizedVariablesForNode().length === 0 && (
										<div className="py-8 text-center text-gray-500 dark:text-gray-400">
											<p className="mb-2 text-sm">
												No upstream data available
											</p>
											<p className="text-xs">
												Connect this node to a submission form or other data
												source to see available variables.
											</p>
										</div>
									)}
								</div>
							</div>
						)}
					</div>

					{/* Content */}
					<div className="overflow-auto flex-1 p-6 bg-white transition-colors dark:bg-gray-900">
						{activeSection === 'basic' && (
							<div className="space-y-6">
								{/* Storage Configuration Banner */}
								{config.storeAs && (
									<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
										<div className="flex gap-3 items-start">
											<div className="flex-shrink-0 w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5">
												🔐
											</div>
											<div>
												<h4 className="font-medium text-blue-900 dark:text-blue-100">
													Custom Storage Key
												</h4>
												<p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
													Response data will be stored as:{' '}
													<code className="px-1 bg-blue-100 rounded dark:bg-blue-800">
														{'{{' + config.storeAs + '.*}}'}
													</code>
												</p>
											</div>
										</div>
									</div>
								)}

								{/* Storage Key Configuration */}
								<div className="space-y-2">
									<label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
										Storage Key (Optional)
									</label>
									<input
										type="text"
										value={config.storeAs || ''}
										onChange={e => handleChange({ storeAs: e.target.value })}
										placeholder="api_data (defaults to node ID)"
										className="px-4 py-3 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 dark:bg-gray-800 dark:text-white"
									/>
									<div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
										<p>
											<strong>How it works:</strong> Downstream nodes will
											access this API's response using your custom key.
										</p>
										<div className="p-2 bg-gray-50 rounded border dark:bg-gray-800">
											<p className="mb-1">
												<strong>Examples:</strong>
											</p>
											<div className="space-y-1 font-mono text-xs">
												<div>
													• Storage key:{' '}
													<span className="text-blue-600 dark:text-blue-400">
														user_data
													</span>{' '}
													→ Access:{' '}
													<span className="text-green-600 dark:text-green-400">
														{'{{user_data.name}}'}
													</span>
												</div>
												<div>
													• Storage key:{' '}
													<span className="text-blue-600 dark:text-blue-400">
														quote
													</span>{' '}
													→ Access:{' '}
													<span className="text-green-600 dark:text-green-400">
														{'{{quote.amount}}'}
													</span>
												</div>
												<div>
													• No custom key → Access:{' '}
													<span className="text-green-600 dark:text-green-400">
														{'{{' + nodeId + '.data}}'}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Rest of basic tab content would go here - abbreviated for brevity */}
								<div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
									<p className="text-sm text-blue-800 dark:text-blue-200">
										💡 <strong>Tip:</strong> Switch to Wizard Mode above for a
										guided step-by-step setup experience!
									</p>
								</div>
							</div>
						)}

						{/* Other advanced mode sections would be rendered here */}
						{activeSection !== 'basic' && (
							<div className="py-12 text-center">
								<div className="mb-4 text-6xl">🧙‍♂️</div>
								<h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
									Try Wizard Mode!
								</h3>
								<p className="mb-4 text-gray-600 dark:text-gray-400">
									For a better experience, switch to Wizard Mode using the toggle
									above.
								</p>
								<button
									onClick={() => setWizardMode(true)}
									className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
								>
									Switch to Wizard Mode
								</button>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
